/**
 * Related articles horizontal slider with prev/next arrows.
 */
class BlogRelatedSlider extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-blog-related-track]');
    this.#prev = this.querySelector('[data-blog-related-prev]');
    this.#next = this.querySelector('[data-blog-related-next]');

    if (!this.#track || !this.#prev || !this.#next) return;

    this.#prev.addEventListener('click', this.#onPrev);
    this.#next.addEventListener('click', this.#onNext);
    this.#track.addEventListener('scroll', this.#updateButtons, { passive: true });
    window.addEventListener('resize', this.#updateButtons, { passive: true });

    requestAnimationFrame(() => this.#updateButtons());
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener('click', this.#onPrev);
    this.#next?.removeEventListener('click', this.#onNext);
    this.#track?.removeEventListener('scroll', this.#updateButtons);
    window.removeEventListener('resize', this.#updateButtons);
  }

  #onPrev = () => {
    this.#scrollBy(-1);
  };

  #onNext = () => {
    this.#scrollBy(1);
  };

  /**
   * @param {number} direction
   */
  #scrollBy(direction) {
    if (!this.#track) return;
    this.#track.scrollBy({ left: this.#getScrollAmount() * direction, behavior: 'smooth' });
    window.setTimeout(() => this.#updateButtons(), 350);
  }

  #getScrollAmount() {
    if (!this.#track) return 0;
    const card = this.#track.querySelector('.blog-related-card');
    if (!(card instanceof HTMLElement)) {
      return Math.max(this.#track.clientWidth * 0.85, 240);
    }
    const styles = window.getComputedStyle(this.#track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 16;
    return card.offsetWidth + gap;
  }

  #updateButtons = () => {
    if (!this.#track || !this.#prev || !this.#next) return;

    const maxScroll = this.#track.scrollWidth - this.#track.clientWidth;
    const atStart = this.#track.scrollLeft <= 2;
    const atEnd = this.#track.scrollLeft >= maxScroll - 2;
    const canScroll = maxScroll > 4;

    this.#prev.disabled = !canScroll || atStart;
    this.#next.disabled = !canScroll || atEnd;
    this.classList.toggle('is-scrollable', canScroll);
  };
}

if (!customElements.get('blog-related-slider')) {
  customElements.define('blog-related-slider', BlogRelatedSlider);
}
